import PanelError from '@wandb/weave/common/components/elements/PanelError';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  constString,
  maybeOpArray,
  opArtifactMembershipForAlias,
  opProjectArtifact,
  opRootProject,
  OutputNode,
} from '@wandb/weave/core';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {ErrorBoundary} from '../components/ErrorBoundary';
import {captureError} from '../integrations';
import * as Url from '../util/urls';
import {FilesPanel, PanelFilesConfig} from './Panel2/PanelFiles';

interface BaseFilePathArgs {
  entityName: string;
  projectName: string;
  artifactSequenceName: string;
  artifactCommitHash: string;
  compareArtifactID?: string;
  compareArtifactSequence?: string;
}

interface ArtifactFilesProps {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  artifactCommitHash: string;
  filePath?: string;
  setFilePath: (base: BaseFilePathArgs) => (newPathArray: string[]) => void;
}

function renderPanelError() {
  return (
    <div style={{padding: 40}}>
      <PanelError
        message={
          <div>
            Oops, something went wrong. If this keeps happening, message
            support@wandb.com with a link to this page
          </div>
        }
      />
    </div>
  );
}
function logError(error: Error) {
  console.error('ArtifactFiles error', error);
  captureError(error, 'ArtifactFiles error');
}

const ArtifactFiles = (props: ArtifactFilesProps) => {
  const {
    entityName,
    projectName,
    artifactTypeName,
    artifactCollectionName: artifactSequenceName,
    artifactCommitHash,
    filePath,
    setFilePath,
  } = props;

  const location = useLocation();
  const hash = location.hash.slice(1);
  let compareID: string | undefined;
  let compareSequenceName: string | undefined;
  if (hash.length > 0) {
    const [compareId, compareSN] = hash.split('$');
    compareID = compareId;
    compareSequenceName = compareSN;
  }

  const [context, setContext] = useState<Panel2.PanelContext>({});
  const updateContext = useCallback<Panel2.UpdateContext>(
    newContext => {
      setContext({...context, ...newContext});
    },
    [context]
  );

  const callSetFilePath = useCallback(
    (path: string[]) =>
      setFilePath({
        entityName,
        projectName,
        artifactSequenceName,
        artifactCommitHash,
        compareArtifactID: compareID,
        compareArtifactSequence: compareSequenceName,
      })(path),
    [
      entityName,
      projectName,
      artifactSequenceName,
      artifactCommitHash,
      compareID,
      compareSequenceName,
      setFilePath,
    ]
  );

  const updateConfig = useCallback(
    (newConfig: PanelFilesConfig) => callSetFilePath(newConfig.path || []),
    [callSetFilePath]
  );

  const config = useMemo(() => {
    return {path: Url.parseRunTabPath(filePath)};
  }, [filePath]);
  const artifacts = useMemo(() => {
    const artifactOps = [
      opArtifactMembershipForAlias({
        artifact: opProjectArtifact({
          project: opRootProject({
            entityName: constString(entityName),
            projectName: constString(projectName),
          }),
          artifactName: constString(artifactSequenceName),
        }),
        aliasName: constString(artifactCommitHash),
      }),
    ];
    if (compareID != null) {
      artifactOps.push(
        opArtifactMembershipForAlias({
          artifact: opProjectArtifact({
            project: opRootProject({
              entityName: constString(entityName),
              projectName: constString(projectName),
            }),
            artifactName: constString(
              compareSequenceName ?? artifactSequenceName
            ),
          }),
          aliasName: constString(compareID),
        })
      );
    }
    return maybeOpArray(artifactOps as any) as OutputNode<{
      type: 'list';
      objectType: 'artifactMembership';
    }>;
  }, [
    entityName,
    projectName,
    artifactSequenceName,
    artifactCommitHash,
    compareSequenceName,
    compareID,
  ]);

  return (
    <ErrorBoundary onError={logError} renderError={renderPanelError}>
      <FilesPanel
        config={config}
        configMode={false}
        loading={false}
        input={artifacts}
        context={context}
        updateConfig={updateConfig}
        updateContext={updateContext}
      />
    </ErrorBoundary>
  );
};

export default ArtifactFiles;
